import { getEnvVar } from "./env";
import { CONSENT_COOKIE } from "./cookie-utils";
import { measureOpenAiPageView, setOpenAiPixelConsent } from "./openAiPixel";
import { getCookie } from "./utils";

/** GA4 Measurement ID for www.caire.se (property 512786677, stream 15290479372). */
export const CAIRE_GA_MEASUREMENT_ID = "G-FDJ6P40BB0";

/**
 * Resolve the caire.se marketing Measurement ID from env, with the live stream as fallback.
 */
export function resolveCaireGaMeasurementId(): string {
  return (
    getEnvVar("VITE_GA_ID") ||
    getEnvVar("VITE_GA_MEASUREMENT_ID") ||
    CAIRE_GA_MEASUREMENT_ID
  );
}

// Type for gtag arguments: gtag("js", new Date()), gtag("config", id, {...}),
// gtag("event", name, {...}) and gtag("consent", "default" | "update", {...}).
type GtagArg =
  | string
  | number
  | boolean
  | Date
  | Record<string, unknown>
  | GtagArg[]
  | undefined;

// Type definitions for Google Analytics
declare global {
  interface Window {
    // gtag.js reads commands only as Arguments objects; GTM pushes plain objects.
    dataLayer: Array<IArguments | GtagArg>;
    gtag: (...args: GtagArg[]) => void;
    gaInitialized?: boolean;
    checkGA?: () => void;
  }
}

export type EventParams = Record<string, string | number | boolean>;

/** Query parameters GA4 reads for attribution; any other query data stays out. */
const ATTRIBUTION_PARAM = /^(utm_[a-z]+|gclid|gbraid|wbraid|dclid)$/;

/**
 * GA4 derives session source, medium and campaign (utm_*) and the Google Ads
 * click (gclid) from page_location. Sending only origin + path dropped them,
 * so every tagged ad click arrived as direct or unassigned. Keep the
 * attribution parameters of the current URL and nothing else.
 */
export function gaPageLocation(
  pathname: string = window.location.pathname,
): string {
  const kept = new URLSearchParams();
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (ATTRIBUTION_PARAM.test(key)) kept.append(key, value);
  });
  const query = kept.toString();
  return `${window.location.origin}${pathname}${query ? `?${query}` : ""}`;
}

/**
 * Google's queue function, the same one the SSR head snippet (server.ts)
 * defines. gtag.js processes only the Arguments object and silently ignores an
 * array with the same values. This module used to push arrays, so on
 * www.caire.se the consent default below never reached Google, and a
 * client-only render (vite dev) sent no GA4 hits at all (found 2026-09-25).
 */
const queueGtag = function gtag() {
  // eslint-disable-next-line prefer-rest-params -- gtag.js reads `arguments`, never an array
  window.dataLayer.push(arguments);
} as (...args: GtagArg[]) => void;

/**
 * Consent mode default, set before any hit. Everything is denied until the
 * visitor accepts all in the banner (`CONSENT_COOKIE` = "all"); a returning
 * visitor who already did starts granted, as in the SSR head (server.ts). While
 * denied, gtag.js sets no cookies and sends only cookieless pings that Google
 * uses for modelling.
 */
const setInitialConsent = (): void => {
  if (typeof window === "undefined") return;

  // Initialize dataLayer immediately if not exists
  window.dataLayer = window.dataLayer || [];

  const granted = getCookie(CONSENT_COOKIE) === "all" ? "granted" : "denied";
  queueGtag("consent", "default", {
    analytics_storage: granted,
    ad_storage: granted,
    ad_user_data: granted,
    ad_personalization: "denied",
    wait_for_update: 500,
  });

  window.gtag = window.gtag || queueGtag;
};

// Call this immediately when module loads
if (typeof window !== "undefined") {
  setInitialConsent();
}

/**
 * Initialize Google Analytics
 * Call this function at application startup or in the main layout
 */
export const initGA = (): void => {
  if (typeof window === "undefined") return;
  const measurementId = resolveCaireGaMeasurementId();
  if (!measurementId) return;
  if (!window.gtag) setInitialConsent();

  if (!window.gaInitialized) {
    window.gtag("js", new Date());
    window.gtag("config", measurementId, {
      anonymize_ip: true,
      cookie_flags: "SameSite=None;Secure",
      send_page_view: false,
      page_location: gaPageLocation(),
    });
    window.gaInitialized = true;
  }

  // A queued gtag function is not proof its transport script exists.
  if (document.querySelector(`script[src*="gtag/js?id=${measurementId}"]`))
    return;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.onerror = () => {
    console.warn("Google Analytics transport unavailable");
  };
  document.head.appendChild(script);
};

/**
 * Update consent for analytics
 */
export const updateConsent = (analyticsConsent: boolean): void => {
  if (typeof window === "undefined" || !window.gtag) {
    console.warn("gtag not available for consent update");
    return;
  }

  console.log("Updating consent:", {
    analytics_storage: analyticsConsent ? "granted" : "denied",
    ad_storage: analyticsConsent ? "granted" : "denied",
    ad_user_data: analyticsConsent ? "granted" : "denied",
    ad_personalization: "denied",
  });

  window.gtag("consent", "update", {
    analytics_storage: analyticsConsent ? "granted" : "denied",
    ad_storage: analyticsConsent ? "granted" : "denied",
    // Ad conversions (Google Ads) need ad_user_data; it follows the banner's
    // "accept all". No remarketing, so personalization stays denied.
    ad_user_data: analyticsConsent ? "granted" : "denied",
    ad_personalization: "denied",
  });
  setOpenAiPixelConsent(analyticsConsent);
  // The pixel may have loaded just now; route tracking already deduped this
  // page for Google, so report it to OpenAI directly.
  if (analyticsConsent)
    measureOpenAiPageView(window.location.pathname, document.title);

  // If consent is granted, send a page view to ensure tracking starts immediately
  if (analyticsConsent) {
    trackPageView(window.location.pathname, document.title);
  }
};

/**
 * Track a custom event in Google Analytics
 * @param eventName - Name of the event to track
 * @param params - Optional parameters to include with the event
 */
export const trackEvent = (eventName: string, params?: EventParams): void => {
  if (typeof window === "undefined" || !window.gtag) return;

  try {
    window.gtag("event", eventName, {
      ...params,
      send_to: resolveCaireGaMeasurementId(),
      page_location: gaPageLocation(),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error tracking event:", error);
  }
};

/**
 * Track page view in Google Analytics
 * @param path - Path of the page being viewed
 * @param title - Title of the page being viewed
 */
let lastPageViewPath: string | undefined;

export const trackPageView = (path: string, title: string): void => {
  if (typeof window === "undefined" || !window.gtag) return;
  const measurementId = resolveCaireGaMeasurementId();
  if (!measurementId) return;
  const pathname = new URL(path, window.location.origin).pathname;
  // Route tracking, page-specific hooks and consent restoration share one
  // owner. A -> B -> A remains three views; StrictMode/title changes do not.
  if (lastPageViewPath === pathname) return;
  lastPageViewPath = pathname;
  try {
    window.gtag("event", "page_view", {
      send_to: measurementId,
      page_title: title,
      page_location: gaPageLocation(pathname),
      page_path: pathname,
    });
    measureOpenAiPageView(pathname, title);
  } catch (error) {
    lastPageViewPath = undefined;
    console.error("Error tracking page view:", error);
  }
};

/**
 * Track form submission events
 * @param formName - Name/identifier of the form
 * @param success - Whether the submission was successful
 */
export const trackFormSubmission = (
  formName: string,
  success: boolean,
): void => {
  trackEvent("form_submission", {
    form_name: formName,
    success: success,
    page_path: window.location.pathname,
  });
};

/**
 * Track user engagement events
 * @param action - The action performed (e.g., 'click', 'hover')
 * @param label - Label for the interaction
 * @param value - Optional numeric value
 */
export const trackEngagement = (
  action: string,
  label: string,
  value?: number,
): void => {
  trackEvent("user_engagement", {
    action,
    label,
    page_path: window.location.pathname,
    ...(value !== undefined && { value }),
  });
};

/**
 * Track error events
 * @param errorType - Type of error
 * @param errorMessage - Error message
 * @param errorLocation - Where the error occurred
 */
export const trackError = (
  errorType: string,
  errorMessage: string,
  errorLocation: string,
): void => {
  trackEvent("error", {
    error_type: errorType,
    error_message: errorMessage,
    error_location: errorLocation,
    page_path: window.location.pathname,
  });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (ctaName: string, location: string): void => {
  trackEvent("cta_click", {
    cta_name: ctaName,
    cta_location: location,
    page_path: window.location.pathname,
  });
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = (
  featureName: string,
  action: string,
): void => {
  trackEvent("feature_usage", {
    feature_name: featureName,
    feature_action: action,
    page_path: window.location.pathname,
  });
};

/**
 * Track VadArCaire page interactions
 */
export const trackVadArCaireInteraction = (
  action: string,
  section: string,
  details?: string,
): void => {
  trackEvent("vad_ar_caire_interaction", {
    interaction_action: action,
    page_section: section,
    interaction_details: details || "",
    page_path: window.location.pathname,
  });
};

/**
 * Track internal link clicks from VadArCaire page
 */
export const trackVadArCaireInternalLink = (
  linkText: string,
  destination: string,
  section: string,
): void => {
  trackEvent("vad_ar_caire_internal_link", {
    link_text: linkText,
    link_destination: destination,
    source_section: section,
    page_path: window.location.pathname,
  });
};

// SEO Event Tracking
export const trackSeoImpression = (
  pageType: string,
  seoData: {
    title: string;
    url: string;
    source?: string;
  },
): void => {
  trackEvent("seo_impression", {
    page_type: pageType,
    page_title: seoData.title,
    page_url: seoData.url,
    source: seoData.source || "direct",
  });
};

export const trackArticleEngagement = (articleData: {
  title: string;
  author: string;
  category?: string;
  readPercentage: number;
  timeSpent: number;
  scrollDepth: number;
}): void => {
  trackEvent("article_engagement", {
    title: articleData.title,
    author: articleData.author,
    category: articleData.category || "uncategorized",
    read_percentage: articleData.readPercentage,
    time_spent: articleData.timeSpent,
    scroll_depth: articleData.scrollDepth,
  });
};

export const trackSocialShare = (
  platform: string,
  content: {
    title: string;
    url: string;
    type: "article" | "page" | "resource";
  },
): void => {
  trackEvent("social_share", {
    platform,
    content_title: content.title,
    content_url: content.url,
    content_type: content.type,
  });
};

// SEO Performance
export const trackSeoPerformanceMetrics = (data: {
  lcp: number;
  fid: number;
  cls: number;
  ttfb: number;
  fcp: number;
  pageUrl: string;
}): void => {
  trackEvent("seo_performance", {
    lcp: data.lcp,
    fid: data.fid,
    cls: data.cls,
    ttfb: data.ttfb,
    fcp: data.fcp,
    page_url: data.pageUrl,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Verify Google Analytics is working
 * Call this from browser console: window.checkGA()
 */
export const checkGAStatus = (): void => {
  if (typeof window === "undefined") {
    console.log("❌ Not in browser environment");
    return;
  }

  const measurementId = resolveCaireGaMeasurementId();

  console.log("=== Google Analytics Status Check ===");
  console.log("Measurement ID:", measurementId);
  console.log(
    "Environment Variable:",
    measurementId || "Not set (set VITE_GA_MEASUREMENT_ID for caire.se)",
  );
  console.log(
    "gtag function:",
    typeof window.gtag === "function" ? "✅ Available" : "❌ Not available",
  );
  console.log("gaInitialized:", window.gaInitialized ? "✅ Yes" : "❌ No");
  console.log(
    "dataLayer:",
    Array.isArray(window.dataLayer)
      ? `✅ Available (${window.dataLayer.length} items)`
      : "❌ Not available",
  );

  const scriptLoaded = document.querySelector(
    `script[src*="${measurementId}"]`,
  );
  console.log("GA Script loaded:", scriptLoaded ? "✅ Yes" : "❌ No");

  if (window.gtag && typeof window.gtag === "function") {
    // If gtag is available but not initialized, try to initialize it
    if (!window.gaInitialized) {
      console.log(
        "⚠️  gtag available but not initialized. Attempting to initialize...",
      );
      const mid = resolveCaireGaMeasurementId();
      if (mid) {
        window.gtag("js", new Date());
        window.gtag("config", mid, {
          anonymize_ip: true,
          cookie_flags: "SameSite=None;Secure",
          send_page_view: false,
          page_title: document.title,
          page_location: window.location.href,
        });
        window.gaInitialized = true;
        console.log("✅ Google Analytics initialized!");
      }
    } else {
      console.log("✅ Google Analytics appears to be working!");
    }
    console.log(
      "Test event sent - check GA Real-Time reports in a few minutes",
    );
    window.gtag("event", "ga_status_check", {
      check_time: new Date().toISOString(),
      page_path: window.location.pathname,
    });
  } else {
    console.log("❌ Google Analytics may not be initialized properly");
    console.log("💡 Try refreshing the page or run: initGA()");
  }
  console.log("===================================");
};

// Make it available globally for easy debugging
declare global {
  interface Window {
    checkGA?: () => void;
  }
}

// Expose checkGA function globally - run this immediately when module loads
if (typeof window !== "undefined") {
  // Use Object.defineProperty to ensure it's not overwritten
  Object.defineProperty(window, "checkGA", {
    value: checkGAStatus,
    writable: true,
    configurable: true,
    enumerable: true,
  });

  // Also try direct assignment as fallback
  window.checkGA = checkGAStatus;
}
